import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout, teamLeaderNavItems, workerNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  MapPin,
  Calendar,
  Users,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowRight,
  Briefcase,
  FileText,
  Building2,
  HourglassIcon
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamMyProjects() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [acceptedProjects, setAcceptedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  
  const navItems = user?.role === "team_leader" ? teamLeaderNavItems : workerNavItems;
  
  // Get the correct route prefix based on user role
  const getProjectRoute = (projectId) => {
    if (user?.role === "worker") {
      return `/worker/project/${projectId}`;
    }
    return `/team-leader/my-projects/${projectId}`;
  };
  
  // Get the correct sign contract route based on user role
  const getSignRoute = (projectId) => {
    if (user?.role === "worker") {
      return `/worker/sign-contract/${projectId}`;
    }
    return `/team-leader/sign-contract/${projectId}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all applications
      const appsRes = await axios.get(`${API}/team/my-applications`);
      setApplications(appsRes.data);
      
      // Fetch accepted projects with full details
      const projectsRes = await axios.get(`${API}/team/my-projects`);
      setAcceptedProjects(projectsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", icon: <HourglassIcon className="w-3 h-3" />, label: t('status.pending') },
      accepted: { color: "bg-green-500/20 text-green-400 border-green-500/30", icon: <CheckCircle2 className="w-3 h-3" />, label: t('status.accepted') },
      selected: { color: "bg-green-500/20 text-green-400 border-green-500/30", icon: <CheckCircle2 className="w-3 h-3" />, label: t('status.selected') },
      rejected: { color: "bg-red-500/20 text-red-400 border-red-500/30", icon: <XCircle className="w-3 h-3" />, label: t('status.rejected') },
      not_selected: { color: "bg-red-500/20 text-red-400 border-red-500/30", icon: <XCircle className="w-3 h-3" />, label: t('status.notSelected') }
    };
    const config = configs[status] || configs.pending;
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const pendingApps = applications.filter(a => a.status === "pending");
  const rejectedApps = applications.filter(a => a.status === "rejected" || a.status === "not_selected");
  const acceptedApps = applications.filter(a => a.status === "accepted" || a.status === "selected");

  if (loading) {
    return (
      <DashboardLayout navItems={navItems} title={t('nav.myProjects')}>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={navItems} title={t('nav.myProjects')}>
      <div className="space-y-6" data-testid="team-my-projects">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-['Unbounded'] font-bold text-white">{t('nav.myProjects')}</h1>
            <p className="text-[#94A3B8]">{t('projects.trackApplications')}</p>
          </div>
          <div className="flex gap-2">
            <Badge className="bg-yellow-500/20 text-yellow-400">{pendingApps.length} {t('status.pending')}</Badge>
            <Badge className="bg-green-500/20 text-green-400">{acceptedApps.length} {t('status.accepted')}</Badge>
            <Badge className="bg-red-500/20 text-red-400">{rejectedApps.length} {t('status.rejected')}</Badge>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#0A0A0A] border border-white/10">
            <TabsTrigger value="all" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              {t('common.all')} ({applications.length})
            </TabsTrigger>
            <TabsTrigger value="accepted" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">
              {t('status.active')} ({acceptedApps.length})
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-[#050505]">
              {t('status.pending')} ({pendingApps.length})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
              {t('status.rejected')} ({rejectedApps.length})
            </TabsTrigger>
          </TabsList>

          {/* All Applications */}
          <TabsContent value="all" className="mt-6">
            {applications.length === 0 ? (
              <EmptyState message={t('projects.noApplicationsYet')} />
            ) : (
              <div className="space-y-4">
                {applications.map((app) => {
                  // Find the team contract for this application if it's accepted
                  const projectData = acceptedProjects.find(p => p.project?.project_id === app.project_id);
                  const teamContract = projectData?.team_contract;
                  
                  return (
                    <ApplicationCard 
                      key={app.application_id} 
                      app={app} 
                      navigate={navigate}
                      getStatusBadge={getStatusBadge}
                      teamContract={teamContract}
                      getProjectRoute={getProjectRoute}
                      getSignRoute={getSignRoute}
                    />
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Active/Accepted Projects */}
          <TabsContent value="accepted" className="mt-6">
            {acceptedApps.length === 0 ? (
              <EmptyState message="No accepted projects yet. Keep applying!" />
            ) : (
              <div className="space-y-4">
                {acceptedProjects.map((item) => (
                  <ActiveProjectCard 
                    key={item.project?.project_id} 
                    item={item} 
                    navigate={navigate}
                    getProjectRoute={getProjectRoute}
                    getSignRoute={getSignRoute}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Pending Applications */}
          <TabsContent value="pending" className="mt-6">
            {pendingApps.length === 0 ? (
              <EmptyState message={t('applications.noPendingApplications')} />
            ) : (
              <div className="space-y-4">
                {pendingApps.map((app) => (
                  <ApplicationCard 
                    key={app.application_id} 
                    app={app} 
                    navigate={navigate}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Rejected Applications */}
          <TabsContent value="rejected" className="mt-6">
            {rejectedApps.length === 0 ? (
              <EmptyState message={t('applications.noRejectedApplications')} />
            ) : (
              <div className="space-y-4">
                {rejectedApps.map((app) => (
                  <ApplicationCard 
                    key={app.application_id} 
                    app={app} 
                    navigate={navigate}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Browse More Projects CTA - For both Team Leaders and Workers */}
        <Card className="bg-[#23F7DD]/5 border-[#23F7DD]/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Briefcase className="w-6 h-6 text-[#23F7DD]" />
                <div>
                  <p className="text-white font-medium">{t('projects.lookingForMoreWork')}</p>
                  <p className="text-sm text-[#94A3B8]">
                    {user?.role === "worker" 
                      ? t('projects.browseAsWorker')
                      : t('projects.browseAndApply')
                    }
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate(user?.role === "worker" ? "/worker/browse-projects" : "/team-leader/browse-projects")}
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
              >
                {t('nav.browseProjects')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

// Empty State Component
function EmptyState({ message }) {
  return (
    <Card className="bg-[#0A0A0A] border-white/10">
      <CardContent className="py-12 text-center">
        <Building2 className="w-16 h-16 mx-auto mb-4 text-[#475569]" />
        <p className="text-[#94A3B8]">{message}</p>
      </CardContent>
    </Card>
  );
}

// Application Card Component
function ApplicationCard({ app, navigate, getStatusBadge, teamContract, getProjectRoute, getSignRoute }) {
  const { t } = useTranslation();
  const project = app.project;
  const isAccepted = app.status === "accepted" || app.status === "selected";
  const isV3 = app.source === "v3";
  
  // Check if this accepted application needs attention (unsigned contract)
  const needsSignature = isAccepted && (
    (teamContract && !teamContract.all_signed) || 
    (isV3 && app.project_status === "team_selected")
  );
  
  return (
    <Card className={`bg-[#0A0A0A] border-white/10 hover:border-white/20 transition-all relative ${isAccepted ? 'border-green-500/30' : ''} ${needsSignature ? 'ring-2 ring-orange-500/50' : ''}`}>
      {/* Attention Badge */}
      {needsSignature && (
        <div className="absolute -top-2 -right-2 z-10">
          <div className="bg-orange-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold animate-pulse shadow-lg">
            <FileText className="w-4 h-4" />
          </div>
        </div>
      )}
      
      <CardContent className="p-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Project Info */}
          <div className="flex-1">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <h3 className="text-lg font-['Unbounded'] font-semibold text-white">
                  {project?.title || "Project"}
                </h3>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-[#94A3B8]">
                  {project?.location_city && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-[#23F7DD]" />
                      {project.location_city}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-[#23F7DD]" />
                    {t('applications.applicationDate')} {(app.created_at || app.applied_at) ? new Date(app.created_at || app.applied_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
              {getStatusBadge(app.status)}
            </div>

            {app.message && (
              <p className="text-sm text-[#94A3B8] mt-2 italic">&ldquo;{app.message}&rdquo;</p>
            )}

            <div className="flex items-center gap-4 mt-3 text-sm">
              <span className="text-[#94A3B8]">
                Status: <span className="text-white capitalize">{project?.status?.replace("_", " ") || app.project_status?.replace("_", " ")}</span>
              </span>
            </div>
            
            {/* Contract Warning for selected applications needing signature */}
            {needsSignature && (
              <div className="mt-3 p-2 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-center gap-2">
                <FileText className="w-4 h-4 text-orange-400" />
                <span className="text-sm text-orange-400">You need to sign the team contract</span>
              </div>
            )}
          </div>

          {/* Action */}
          <div className="flex items-center gap-3">
            {needsSignature && (
              <Button 
                onClick={() => navigate(getSignRoute ? getSignRoute(app.project_id) : `/team-leader/sign-contract/${app.project_id}`)}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
              >
                <FileText className="w-4 h-4 mr-2" />
                {t('contract.signContract')}
              </Button>
            )}
            {isAccepted && !needsSignature && (
              <Button 
                onClick={() => navigate(getProjectRoute ? getProjectRoute(app.project_id) : `/team-leader/my-projects/${app.project_id}`)}
                className="bg-green-500 hover:bg-green-600 text-white font-bold"
              >
                {t('projects.openProject')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Active Project Card Component (with more details)
function ActiveProjectCard({ item, navigate, getProjectRoute, getSignRoute }) {
  const { t } = useTranslation();
  const { project, team_contract, application, source, needs_signature } = item;
  const isV3 = source === "v3";
  
  const needsSignature = needs_signature || (team_contract && !team_contract.all_signed);
  
  return (
    <Card className={`bg-[#0A0A0A] border-green-500/30 hover:border-green-500/50 transition-all relative ${needsSignature ? 'ring-2 ring-orange-500/50' : ''}`}>
      {/* Attention Badge */}
      {needsSignature && (
        <div className="absolute -top-2 -right-2 z-10">
          <div className="bg-orange-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold animate-pulse shadow-lg">
            <FileText className="w-4 h-4" />
          </div>
        </div>
      )}
      
      <CardContent className="p-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Project Info */}
          <div className="flex-1">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-['Unbounded'] font-semibold text-white">
                    {project?.title}
                  </h3>
                  <Badge className={`${needsSignature ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : 'bg-green-500/20 text-green-400 border-green-500/30'} border`}>
                    {needsSignature ? (
                      <><FileText className="w-3 h-3 mr-1" />{t('contract.awaitingSignature')}</>
                    ) : (
                      <><CheckCircle2 className="w-3 h-3 mr-1" />{t('status.active')}</>
                    )}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-[#94A3B8]">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-[#23F7DD]" />
                    {project?.location_city}
                  </span>
                  {project?.start_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-[#23F7DD]" />
                      {t('projects.starts')} {new Date(project.start_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats - Only show Team Net, not project budget */}
            <div className="flex flex-wrap items-center gap-4 mt-3">
              {(team_contract?.net_to_team || team_contract?.net_amount || project?.net_to_team) && (
                <div className="px-3 py-1 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg">
                  <span className="text-xs text-[#94A3B8]">{t('contract.netToTeam')}</span>
                  <p className="text-sm font-bold text-[#23F7DD]">{(team_contract?.net_to_team || team_contract?.net_amount || project?.net_to_team)?.toLocaleString()} NOK</p>
                </div>
              )}
              <div className="px-3 py-1 bg-white/5 rounded-lg">
                <span className="text-xs text-[#94A3B8]">{t('common.status')}</span>
                <p className="text-sm font-bold text-white capitalize">{project?.status?.replace("_", " ")}</p>
              </div>
            </div>

            {/* Contract Warning */}
            {needsSignature && (
              <div className="mt-3 p-2 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-center gap-2">
                <FileText className="w-4 h-4 text-orange-400" />
                <span className="text-sm text-orange-400">{t('contract.signToStartWorking')}</span>
              </div>
            )}
          </div>

          {/* Action */}
          <div className="flex items-center gap-2">
            {needsSignature ? (
              <Button 
                onClick={() => navigate(getSignRoute ? getSignRoute(project.project_id) : `/team-leader/sign-contract/${project.project_id}`)}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
              >
                <FileText className="w-4 h-4 mr-2" />
                {t('contract.signContract')}
              </Button>
            ) : (
              <Button 
                onClick={() => navigate(getProjectRoute ? getProjectRoute(project.project_id) : `/team-leader/my-projects/${project.project_id}`)}
                className="bg-[#23F7DD] hover:bg-[#70FFEF] text-[#050505] font-bold"
              >
                {t('common.viewDetails')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
